import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

const PLAN_STORAGE_KEY = 'signup_plan_selection'
const VALID_PLANS = ['flex', 'pro', 'pro_plus', 'team', 'enterprise'] as const
const DEFAULT_PLAN = 'flex' as const

export type Plan = (typeof VALID_PLANS)[number]

/**
 * Hook to manage plan selection across signup flow using hybrid approach:
 * - URL query params (primary)
 * - sessionStorage (fallback)
 * - Defaults to 'flex' if no plan is selected
 *
 * This ensures plan selection persists through:
 * - Page refreshes
 * - Email verification flow
 * - Redirect navigation
 *
 * SessionStorage automatically clears when browser/tab closes.
 */
export function usePlanSelection() {
  const [searchParams] = useSearchParams()
  const [selectedPlan, setSelectedPlan] = useState<Plan>(DEFAULT_PLAN)

  useEffect(() => {
    // Try to get plan from URL first (highest priority)
    const planFromUrl = searchParams.get('plan')

    if (planFromUrl && VALID_PLANS.includes(planFromUrl as Plan)) {
      // Store in sessionStorage as backup for when URL might not have it
      sessionStorage.setItem(PLAN_STORAGE_KEY, planFromUrl)
      setSelectedPlan(planFromUrl as Plan)
    } else {
      // Fallback to sessionStorage if URL doesn't have it
      const planFromStorage = sessionStorage.getItem(PLAN_STORAGE_KEY)
      if (planFromStorage && VALID_PLANS.includes(planFromStorage as Plan)) {
        setSelectedPlan(planFromStorage as Plan)
      } else {
        // If no plan in URL or storage, ensure we're using default
        setSelectedPlan(DEFAULT_PLAN)
      }
    }
  }, [searchParams])

  const clearPlan = () => {
    sessionStorage.removeItem(PLAN_STORAGE_KEY)
    setSelectedPlan(DEFAULT_PLAN)
  }

  const setPlan = (plan: Plan) => {
    if (VALID_PLANS.includes(plan)) {
      sessionStorage.setItem(PLAN_STORAGE_KEY, plan)
      setSelectedPlan(plan)
    } else {
      clearPlan()
    }
  }

  return {
    selectedPlan,
    clearPlan,
    setPlan,
    VALID_PLANS
  }
}

/**
 * Utility to check if a plan value is valid
 */
export function isValidPlan(plan: string | null): plan is Plan {
  return plan !== null && VALID_PLANS.includes(plan as Plan)
}

/**
 * Utility to get plan from sessionStorage with 'flex' as fallback
 */
export function getPlanFromStorage(): Plan {
  const plan = sessionStorage.getItem(PLAN_STORAGE_KEY)
  return isValidPlan(plan) ? plan : DEFAULT_PLAN
}

/**
 * Utility to clear plan from sessionStorage
 */
export function clearPlanFromStorage(): void {
  sessionStorage.removeItem(PLAN_STORAGE_KEY)
}
